//** React Imports
import { useEffect, Fragment } from 'react'

// ** Next Imports
import dynamic from 'next/dynamic'

//** Effect Imports
import { useStore } from 'effector-react'

// ** React Imports
import { ReactNode } from 'react'

// ** Source code Imports
import globalStore from 'src/state-manager/global'
import landingStore, { getTableForLanding, getLandingDetail } from 'src/state-manager/landing'
import runtimeStore from 'src/state-manager/runtime'
import tableColumnKeysConf from 'src/configs/db-columns-keys'
import tableEnumV2OptionsConf from 'src/configs/db-enum-v2-options'
import { useSettings } from 'src/@core/hooks/useSettings'
import Spinner from 'src/@core/components/spinner'
import DefaultLayout from 'src/layouts/default-layout'
import newsStore, { getNewsList } from 'src/state-manager/news'

const keyValuesKeys = tableColumnKeysConf.keyValues
const keyValuesEnum = tableEnumV2OptionsConf.keyValues
const howItWorkKeys = tableColumnKeysConf.landing_how_work_it
const howItWorkEnum = tableEnumV2OptionsConf.landing_how_work_it
const artistsKeys = tableColumnKeysConf.landing_artists_slider
const artistsEnum = tableEnumV2OptionsConf.landing_artists_slider
const controlSlidesKeys = tableColumnKeysConf.controls_slides
const controlSlidesEnum = tableEnumV2OptionsConf.controls_slides
const platformSlidesKeys = tableColumnKeysConf.landing_platform_slides
const platformSlidesEnum = tableEnumV2OptionsConf.landing_platform_slides

const LandingPage = dynamic(() => import('src/views/pages/landing-page'), {
  ssr: false
})

const Home = () => {
  const { settings } = useSettings()

  const { language } = useStore(globalStore.locale)

  const { preload: preloadVideo } = useStore(runtimeStore.video)
  const {
    artistsItems,
    landingDetails,
    workItItems,
    platforms,
    platformSlides,
    faqItems,
    controlsSlides,
    partnersItems
  } = useStore(landingStore.landing)
  const { data: newsData } = useStore(newsStore.news)

  useEffect(() => {
    getTableForLanding({
      key: 'artistsItems',
      tableName: 'landing_artists_slider',
      query: {
        $and: [
          {
            [`data.${artistsKeys.language}`]: language === 'ru' ? artistsEnum.language.ru : artistsEnum.language.en
          }
        ]
      }
    })

    getTableForLanding({
      key: 'partnersItems',
      tableName: 'landing_partners_slides'
    })

    getTableForLanding({
      key: 'workItItems',
      tableName: 'landing_how_work_it',
      query: {
        $and: [
          {
            [`data.${howItWorkKeys.language}`]:
              language === 'ru' ? howItWorkEnum.language.ru : howItWorkEnum.language.en
          }
        ]
      }
    })

    getTableForLanding({
      key: 'platformSlides',
      tableName: 'landing_platform_slides',
      query: {
        $and: [
          {
            [`data.${platformSlidesKeys.language}`]:
              language === 'ru' ? platformSlidesEnum.language.ru : platformSlidesEnum.language.en
          }
        ]
      }
    })

    getTableForLanding({
      key: 'faqItems',
      tableName: 'landing_faq'
    })

    getTableForLanding({
      key: 'controlsSlides',
      tableName: 'controls_slides',
      query: {
        $and: [
          {
            [`data.${controlSlidesKeys.language}`]:
              language === 'ru' ? controlSlidesEnum.language.ru : controlSlidesEnum.language.en
          }
        ]
      },
      sort: [{ sort: 'asc', column: controlSlidesKeys.order }]
    })

    getNewsList({
      query: { ['data.showToLanding']: true }
    })

    getLandingDetail({
      query: {
        $or: [{ 'data.groupName': 'landing' }, { 'data.groupName': 'shared' }],
        $and: [
          {
            [`data.${keyValuesKeys.language}`]:
              language === 'ru' ? keyValuesEnum.language.ru : keyValuesEnum.language.en
          }
        ]
      }
    })
  }, [language])

  const isLoading =
    artistsItems.loading ||
    landingDetails.loading ||
    workItItems.loading ||
    platformSlides.loading ||
    faqItems.loading ||
    controlsSlides.loading ||
    newsData.loading ||
    partnersItems.loading ||
    preloadVideo

  return (
    <Fragment>
      {isLoading && <Spinner settings={settings} />}
      <DefaultLayout
        sx={{
          height: isLoading ? 0 : 'auto',
          visibility: isLoading ? 'hidden' : 'visible'
        }}
      >
        <LandingPage
          loading={isLoading}
          artists={artistsItems.data}
          workItItems={workItItems.data}
          platforms={platforms.data}
          platformSlides={platformSlides.data}
          landingDetails={landingDetails.data}
          faqItems={faqItems.data}
          controlsSlides={controlsSlides.data}
          newsData={newsData}
          partnersSlides={partnersItems.data}
        />
      </DefaultLayout>
    </Fragment>
  )
}

Home.getLayout = (page: ReactNode) => page

export default Home
